import { Text } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'

import { useMentionData } from '../context/MentionContext'
import { EngagementHeader } from '../../components/EngagementHeader'
import { MentionHoverActions } from '../../MentionDetails/MentionHoverActions'

import type { JSX } from 'react'

export const MentionHeader_Mention = graphql(/* GraphQL */ `
  fragment MentionHeader_Mention on Mention @_unmask {
    id
    status
    publishedAt
    author {
      name
      username
    }
  }
`)

type MentionHeaderProps = {
  /** Whether this is a context (ancestor) mention rather than the root card. */
  isContextMention?: boolean
  /** Whether this is a reply row below the root mention. */
  isReply?: boolean
}

/**
 * Identity row inside a `MentionCard`'s content slot. Feeds the shared
 * `EngagementHeader`: username (display name fallback) + timestamp, plus a
 * status badge on context mentions and the hover actions (open on network,
 * plus copy link on the root card only).
 *
 * @see `~publish/pages/Community/components/EngagementHeader`
 */
export const MentionHeader = ({
  isContextMention = false,
  isReply = false,
}: MentionHeaderProps): JSX.Element => {
  const mention = useMentionData()
  // `author` is nullable on the GraphQL schema (locked/deleted accounts).
  // The "Private Account" sentinel is set by BE in `author.name` when
  // possible; falling back to 'Unknown author' covers the case where the
  // whole author object is absent.
  const displayName =
    mention.author?.name ?? mention.author?.username ?? 'Unknown author'

  const showStatusBadge =
    isContextMention &&
    (mention.status === 'replied' || mention.status === 'dismissed')
  const statusBadgeText = mention.status === 'replied' ? 'Replied' : 'Resolved'

  return (
    <EngagementHeader
      id={`mention-header-${mention.id}`}
      username={<Text weight="bold">{displayName}</Text>}
      timestamp={mention.publishedAt}
      statusLabel={showStatusBadge ? statusBadgeText : undefined}
      trailing={
        // Context and reply rows get open-on-network but not copy link: the
        // details dialog can only rebuild a thread from the root mention id.
        <MentionHoverActions showCopyLink={!isContextMention && !isReply} />
      }
    />
  )
}

MentionHeader.displayName = 'MentionHeader'
